import { config } from '@/config'
import { authClient } from './client'

function generateRandomString(length: number): string {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('').slice(0, length)
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder()
  return crypto.subtle.digest('SHA-256', encoder.encode(plain))
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach((b) => (binary += String.fromCharCode(b)))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function generateCodeVerifier(): string {
  return generateRandomString(64)
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const hash = await sha256(verifier)
  return base64UrlEncode(hash)
}

const PKCE_VERIFIER_KEY = 'pkce_code_verifier'
const PKCE_STATE_KEY = 'pkce_state'

export async function initiateLogin(): Promise<void> {
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)
  const state = generateRandomString(32)

  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier)
  sessionStorage.setItem(PKCE_STATE_KEY, state)

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.authClientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    scope: 'openid profile email',
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  })

  window.location.href = `${config.authFrontUrl}/login?${params.toString()}`
}

export async function handleCallback(code: string, state: string): Promise<{ accessToken: string }> {
  const savedState = sessionStorage.getItem(PKCE_STATE_KEY)
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)

  if (!savedState || savedState !== state) {
    throw new Error('State invalide')
  }

  if (!codeVerifier) {
    throw new Error('Code verifier manquant')
  }

  sessionStorage.removeItem(PKCE_VERIFIER_KEY)
  sessionStorage.removeItem(PKCE_STATE_KEY)

  const response = await authClient.post('/oauth/token', {
    grantType: 'authorization_code',
    code,
    redirectUri: `${window.location.origin}/auth/callback`,
    clientId: config.authClientId,
    codeVerifier,
  })

  const token =
    response.data.data?.tokens?.accessToken ??
    response.data.data?.accessToken ??
    response.data.accessToken
  return { accessToken: token }
}

export async function refreshToken(): Promise<{ accessToken: string }> {
  const response = await authClient.post('/auth/refresh')
  const token =
    response.data.data?.tokens?.accessToken ??
    response.data.data?.accessToken ??
    response.data.accessToken
  return { accessToken: token }
}

export async function logout(): Promise<void> {
  try {
    await authClient.post('/auth/logout')
  } finally {
    sessionStorage.removeItem(PKCE_VERIFIER_KEY)
    sessionStorage.removeItem(PKCE_STATE_KEY)
  }
}
